export interface CredentialsRecipient {
  email: string;
  login: string;
  password: string;
}

export interface ParsedRecipients {
  recipients: CredentialsRecipient[];
  problems: string[];
}

const REQUIRED_COLUMNS = ['email', 'login', 'password'] as const;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Разбирает одну строку CSV с учётом кавычек: пароли часто содержат
// разделитель, и наивный split по нему тихо ломает данные
const splitRow = (row: string, delimiter: string): string[] => {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < row.length; i += 1) {
    const symbol = row[i];

    if (quoted) {
      if (symbol === '"') {
        // удвоенная кавычка внутри значения
        if (row[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += symbol;
      }
    } else if (symbol === '"') {
      quoted = true;
    } else if (symbol === delimiter) {
      cells.push(cell);
      cell = '';
    } else {
      cell += symbol;
    }
  }

  cells.push(cell);

  return cells.map((value) => value.trim());
};

const detectDelimiter = (header: string): string => {
  const counts = [';', ',', '\t'].map((d) => ({ d, n: header.split(d).length }));
  return counts.sort((a, b) => b.n - a.n)[0].d;
};

export const parseRecipients = (raw: string): ParsedRecipients => {
  const problems: string[] = [];
  // U+FEFF — BOM, который Excel добавляет при экспорте в CSV
  const lines = raw
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    return { recipients: [], problems: ['Список пуст'] };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = splitRow(lines[0], delimiter).map((h) => h.toLowerCase());
  const missing = REQUIRED_COLUMNS.filter((column) => !headers.includes(column));

  if (missing.length > 0) {
    return {
      recipients: [],
      problems: [`В заголовке нет колонок: ${missing.join(', ')}. Найдены: ${headers.join(', ')}`],
    };
  }

  const indexes = {
    email: headers.indexOf('email'),
    login: headers.indexOf('login'),
    password: headers.indexOf('password'),
  };

  const recipients: CredentialsRecipient[] = [];
  const seen = new Map<string, number>();

  lines.slice(1).forEach((line, index) => {
    const lineNumber = index + 2;
    const cells = splitRow(line, delimiter);
    const recipient: CredentialsRecipient = {
      email: cells[indexes.email] ?? '',
      login: cells[indexes.login] ?? '',
      password: cells[indexes.password] ?? '',
    };

    const issues: string[] = [];
    if (!EMAIL_RE.test(recipient.email)) issues.push('некорректный email');
    if (!recipient.login) issues.push('пустой логин');
    if (!recipient.password) issues.push('пустой пароль');

    const key = recipient.email.toLowerCase();
    if (seen.has(key)) issues.push(`дубль адреса, первый раз в строке ${seen.get(key)}`);

    if (issues.length > 0) {
      problems.push(`Строка ${lineNumber} (${recipient.email || 'без адреса'}): ${issues.join('; ')}`);
      return;
    }

    seen.set(key, lineNumber);
    recipients.push(recipient);
  });

  return { recipients, problems };
};
